import React, { ChangeEvent, FC, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { TickIcon } from '../../components/icons/TickIcon';
import './react-phone-input-2-custom.css';
import { createFocusTrap } from 'focus-trap';

const CallingCodeListWrapper = styled.li`
    display: flex;
    text-align: left;
`;

const TickWrapper = styled.span`
    display: flex;
    flex: 5 1 0;
    justify-content: flex-end;
    margin-right: 5px;
`;
interface ICountryCallingCode {
    alpha2Code: string;
    dialCode: string;
    name: string;
}

export interface ICallingCodeSelectorOwnProps {
    countryCode?: string;
    onCountryCodeSet: (country: ICountryCallingCode) => any;
    inputId?: string;
}

export interface ICallingCodeSelectorStateProps {
    countriesCallingCodes: ICountryCallingCode[];
}

type CallingCodeSelectorProps = ICallingCodeSelectorOwnProps & ICallingCodeSelectorStateProps;

type UseFocusTrapOptions = {
    ref: RefObject<HTMLDivElement>;
    isOpen: boolean | undefined;
    onClose: () => void;
};

function useFocusTrap({ ref, isOpen, onClose }: UseFocusTrapOptions): void {
    useEffect(() => {
        if (!isOpen || !ref.current) {
            return;
        }

        const trap = createFocusTrap(ref.current, {
            clickOutsideDeactivates: true,
            escapeDeactivates: true,
            returnFocusOnDeactivate: true,
            onDeactivate: onClose,
        });
        trap.activate();

        return () => {
            trap.deactivate();
        };
    }, [ref, isOpen, onClose]);
}

const getSelectedCountryCode = (
    countryCode: string | undefined,
    countriesCallingCodes: ICountryCallingCode[],
) => {
    return countryCode
        ? countriesCallingCodes.find(
              (country) => country.alpha2Code.toLowerCase() === countryCode.toLowerCase(),
          )
        : undefined;
};

const CallingCodeSelector: FC<CallingCodeSelectorProps> = ({
    countryCode,
    onCountryCodeSet,
    countriesCallingCodes,
    inputId,
}) => {
    const [searchValue, setSearchValue] = useState('');
    const [selectedCountryCode, setSelectedCountryCode] = useState<ICountryCallingCode | undefined>(
        getSelectedCountryCode(countryCode, countriesCallingCodes),
    );
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const dropdownContainerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);
    const itemRefs = useRef<(HTMLButtonElement | null)[]>(
        Array(countriesCallingCodes.length).fill(null),
    );
    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'ArrowDown') {
            setSelectedIndex((prevIndex) =>
                Math.min(prevIndex + 1, countriesCallingCodes.length - 1),
            );
        } else if (event.key === 'ArrowUp') {
            setSelectedIndex((prevIndex) => Math.max(prevIndex - 1, 0));
        }
    };
    const handleClickOutside = (event: Event) => {
        if (
            showDropdown &&
            dropdownRef &&
            dropdownContainerRef &&
            !dropdownContainerRef.current?.contains(event.target as Node)
        ) {
            setShowDropdown(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    useEffect(() => {
        if (itemRefs && selectedIndex >= 0 && itemRefs.current[selectedIndex]) {
            const thisItem = itemRefs.current[selectedIndex];
            thisItem!.focus();
            const top =
                thisItem!.offsetTop -
                dropdownRef.current!.offsetHeight / 2 +
                thisItem!.offsetHeight / 2 -
                40;
            dropdownRef.current!.scrollTop = top;
        }
    }, [selectedIndex]);

    useEffect(() => {
        setSelectedCountryCode(getSelectedCountryCode(countryCode, countriesCallingCodes));
    }, [countryCode, countriesCallingCodes]);

    useEffect(() => {
        selectedCountryCode && onCountryCodeSet(selectedCountryCode);
    }, [selectedCountryCode]);

    const handleFlagDropdownClick = () => {
        setShowDropdown(!showDropdown);
    };

    const handleFlagItemClick = (country: ICountryCallingCode) => {
        setSelectedCountryCode(country);
        onCountryCodeSet(country);
        setShowDropdown(false);
    };

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        const {
            currentTarget: { value: searchValue },
        } = event;
        setSearchValue(searchValue);
    };

    const getCountryDropdownList = () => {
        const searchedCountries = getSearchFilteredCountries();
        const listItems = searchedCountries.map((country, index) => {
            const callingCodeDropdownItem =
                selectedCountryCode && country.alpha2Code === selectedCountryCode.alpha2Code ? (
                    <>
                        <span className="country-name--selected">{country.name}</span>
                        <span className="dial-code--selected">{'+' + country.dialCode}</span>
                        <TickWrapper>
                            <TickIcon></TickIcon>
                        </TickWrapper>
                    </>
                ) : (
                    <>
                        <span className="country-name">{country.name}</span>
                        <span className="dial-code">{'+' + country.dialCode}</span>
                    </>
                );

            return (
                <button
                    key={`flag_no_${index}`}
                    data-flag-key={`flag_no_${index}`}
                    className={`country `} // add highlighted
                    data-dial-code="1"
                    tabIndex={0}
                    ref={(el: HTMLButtonElement) => (itemRefs.current[index] = el)}
                    data-country-code={country.alpha2Code}
                    onClick={() => handleFlagItemClick(country)}
                    style={{ background: 'none', border: 'none', width: '100%' }}
                >
                    <CallingCodeListWrapper>
                        <div className={`flag ${country.alpha2Code.toLowerCase()}`} />
                        {callingCodeDropdownItem}
                    </CallingCodeListWrapper>
                </button>
            );
        });

        return (
            <ul ref={dropdownRef} className={`country-list ${showDropdown ? '' : 'hide'}`}>
                <li className="search">
                    <input
                        className="search-box"
                        id="search-box"
                        type="search"
                        autoFocus={true}
                        value={searchValue}
                        onChange={handleSearchChange}
                    />
                </li>
                {listItems}
            </ul>
        );
    };

    const getSearchFilteredCountries = () => {
        const sanitizedSearchValue = searchValue.trim().toLowerCase();
        return sanitizedSearchValue
            ? countriesCallingCodes.filter(({ name, alpha2Code, dialCode }) =>
                  [`${name}`, `${alpha2Code}`, `+${dialCode}`].some((field) =>
                      field.toLowerCase().includes(sanitizedSearchValue),
                  ),
              )
            : countriesCallingCodes;
    };

    const ref = useRef<HTMLDivElement>(null);
    const onClose = useCallback(() => {
        setShowDropdown(false);
        setTimeout(() => {
            if (inputId) document.getElementById(inputId)?.focus();
        }, 0);
    }, []);
    const isOpen = showDropdown;
    useFocusTrap({ ref, isOpen, onClose });

    return (
        <div className="react-tel-input" ref={ref}>
            <div
                className={`flag-dropdown ${showDropdown ? 'open-dropdown' : ''}`}
                id="flag-dropdown"
                ref={dropdownContainerRef}
            >
                <div className="selected-flag">
                    <button
                        className={`flag ${
                            selectedCountryCode ? selectedCountryCode.alpha2Code.toLowerCase() : ''
                        }`}
                        tabIndex={0}
                        title={
                            selectedCountryCode
                                ? `${selectedCountryCode.name}: + ${selectedCountryCode.dialCode}`
                                : ''
                        }
                        onClick={handleFlagDropdownClick}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                handleFlagDropdownClick();
                            }
                        }}
                    >
                        <div className={`arrow ${showDropdown ? 'up' : ''}`} />
                    </button>
                </div>
                {showDropdown && getCountryDropdownList()}
            </div>
        </div>
    );
};

export default CallingCodeSelector;
